<template>
  <TransitionGroup
    tag="div"
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="transform translate-y-2 opacity-0"
    enter-to-class="transform translate-y-0 opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="transform translate-y-0 opacity-100"
    leave-to-class="transform translate-y-2 opacity-0"
    class="fixed top-0 right-0 z-50 p-4 space-y-4"
  >
    <div
      v-for="toast in toasts"
      :key="toast.id"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      :class="[
        'max-w-sm w-full shadow-lg rounded-lg pointer-events-auto overflow-hidden',
        toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
      ]"
    >
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <UIIcon
              :name="toast.type === 'error' ? 'x-circle' : 'check-circle'"
              class="h-6 w-6 text-white"
            />
          </div>
          <div class="ml-3 flex-1 pt-0.5">
            <p class="text-sm font-semibold text-white">
              {{ toast.title }}
            </p>
            <p v-if="toast.message" class="mt-1 text-sm text-white text-opacity-90">
              {{ toast.message }}
            </p>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button
              type="button"
              class="rounded-md inline-flex text-white text-opacity-80 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-50"
              @click="removeToast(toast.id)"
            >
              <span class="sr-only">Close</span>
              <UIIcon name="close" class="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </TransitionGroup>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import type { Toast, ToastOptions } from '@/types'
import UIIcon from './UIIcon.vue'

const toasts = ref<Toast[]>([])
const timeouts = new Map<number, number>()

const addToast = (toast: Omit<Toast, 'id'>) => {
  const id = Date.now()
  const newToast = { ...toast, id }
  toasts.value.push(newToast)

  if (toast.duration !== 0) {
    const timeout = window.setTimeout(() => {
      removeToast(id)
    }, toast.duration || 5000)
    timeouts.set(id, timeout)
  }
}

const removeToast = (id: number) => {
  toasts.value = toasts.value.filter(t => t.id !== id)
  const timeout = timeouts.get(id)
  if (timeout) {
    clearTimeout(timeout)
    timeouts.delete(id)
  }
}

// Event handlers
const handleSuccess = (event: CustomEvent) => {
  addToast({
    type: 'success',
    title: event.detail.title,
    message: event.detail.message,
    duration: event.detail.duration
  })
}

const handleError = (event: CustomEvent) => {
  addToast({
    type: 'error',
    title: event.detail.title,
    message: event.detail.message,
    duration: event.detail.duration
  })
}

onMounted(() => {
  window.addEventListener('toast:success', handleSuccess as EventListener)
  window.addEventListener('toast:error', handleError as EventListener)
})

onUnmounted(() => {
  window.removeEventListener('toast:success', handleSuccess as EventListener)
  window.removeEventListener('toast:error', handleError as EventListener)
  timeouts.forEach(clearTimeout)
})

// Export for composition API usage
defineExpose({
  addToast,
  removeToast
})
</script> 